/**
 * StripeButton - Componente pagamento con Stripe
 *
 * Flusso:
 * 1. Al mount → chiama backend createPaymentIntent → riceve client_secret + order_id
 * 2. Inizializza Stripe Elements con il client_secret
 * 3. Mostra PaymentElement (form carta gestito da Stripe, dati NON passano per il backend)
 * 4. Al submit → stripe.confirmPayment() → Stripe addebita la carta
 * 5. Se succeeded → chiama backend confirmPayment → ordine completato nel DB
 * 6. onSuccess(order) → CheckoutPage reindirizza a /order-success/:id
 */

import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createStripePaymentIntent, confirmStripePayment } from '../../api/backend';

// Inizializza Stripe una sola volta fuori dal componente (evita re-inizializzazioni)
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ─────────────────────────────────────────────
// Form interno — deve stare DENTRO <Elements>
// per poter usare useStripe() e useElements()
// ─────────────────────────────────────────────
const StripePaymentForm = ({ orderId, onSuccess, onError }) => {
  const stripe   = useStripe();
  const elements = useElements();

  const [loading, setLoading]       = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Gli hook non sono ancora pronti (Stripe non ha caricato)
    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMessage('');

    // stripe.confirmPayment() invia i dati carta DIRETTAMENTE a Stripe (non al nostro backend)
    // redirect: 'if_required' → rimane sulla pagina se non serve 3D Secure,
    //                           altrimenti reindirizza a Stripe e poi a return_url
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/order-success/${orderId}`,
      },
      redirect: 'if_required',
    });

    if (error) {
      // Errore carta: carta scaduta, fondi insufficienti, ecc.
      setErrorMessage(error.message);
      setLoading(false);
      onError(error);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Pagamento riuscito senza redirect (carta normale senza 3DS)
      // → Ora confermiamo al nostro backend per aggiornare l'ordine nel DB
      try {
        const response = await confirmStripePayment(paymentIntent.id, orderId);
        onSuccess(response.order);
      } catch (err) {
        setErrorMessage('Pagamento ricevuto ma errore nel salvataggio ordine. Contattaci.');
        onError(err);
      }
    }

    // Se arriviamo qui senza error e senza succeeded → l'utente è stato
    // reindirizzato per 3D Secure. Tornerà su /order-success/:id via return_url.

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form">

      {/* PaymentElement: form carta gestito da Stripe (iframe sicuro) */}
      <PaymentElement />

      {/* Messaggio di errore carta */}
      {errorMessage && (
        <div className="stripe-error-message" style={{ color: '#e53935', marginTop: '12px', fontSize: '14px' }}>
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || loading}
        className="theme-btn"
        style={{ width: '100%', marginTop: '20px' }}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" />
            Pagamento in corso...
          </>
        ) : (
          'Paga ora con carta'
        )}
      </button>

      <p style={{ textAlign: 'center', marginTop: '12px', fontSize: '12px', color: '#888' }}>
        🔒 Pagamento sicuro gestito da Stripe
      </p>
    </form>
  );
};

// ─────────────────────────────────────────────
// Componente esterno — crea il PaymentIntent
// e wrappa il form con <Elements>
// ─────────────────────────────────────────────
const StripeButton = ({ shippingData, onSuccess, onError }) => {
  const [clientSecret, setClientSecret] = useState('');
  const [orderId, setOrderId]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');

  useEffect(() => {
    // Chiama il backend per creare il PaymentIntent su Stripe
    // Il backend verifica il carrello, calcola il totale e salva l'ordine pending nel DB
    createStripePaymentIntent(shippingData)
      .then(({ client_secret, order_id }) => {
        setClientSecret(client_secret);
        setOrderId(order_id);
        setLoading(false);
      })
      .catch((err) => {
        const message = err?.error || 'Errore nella preparazione del pagamento. Riprova.';
        setError(message);
        setLoading(false);
        onError(err);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Caricamento form pagamento...</span>
        </div>
        <p style={{ marginTop: '10px', color: '#666' }}>Preparazione pagamento...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: '#e53935', padding: '12px', borderRadius: '6px', background: '#fff5f5' }}>
        <strong>Errore:</strong> {error}
      </div>
    );
  }

  if (!clientSecret) return null;

  // Opzioni per Elements: client_secret + lingua italiana
  const options = {
    clientSecret,
    locale: 'it',
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#c8a97e', // colore brand PetLox
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <StripePaymentForm
        orderId={orderId}
        onSuccess={onSuccess}
        onError={onError}
      />
    </Elements>
  );
};

export default StripeButton;
